// src/components/kokam/SubirExcelInscritos.jsx

import { useState, useRef } from "react";
import {
  Card, Typography, Button, Modal, Progress, Space, Upload, message,
} from "antd";
import {
  UploadOutlined, CheckCircleFilled, SyncOutlined,
} from "@ant-design/icons";
import { subirExcelInscritos } from "../../services/reporteKokam/temporalKokamInscribirService";

const { Title, Text } = Typography;

const MENSAJES_PROGRESO = [
  "Leyendo archivo Excel...",
  "Validando estructura de columnas...",
  "Procesando filas del reporte...",
  "Insertando hogares en la base de datos...",
  "Guardando inscritos en lotes...",
  "Procesando datos sociodemográficos...",
  "Verificando integridad de los datos...",
  "Finalizando carga...",
];

const ReporteKokamInscribir  = () => {
  const [modalVisible,  setModalVisible]  = useState(false);
  const [progreso,      setProgreso]      = useState(0);
  const [mensajeActual, setMensajeActual] = useState(MENSAJES_PROGRESO[0]);
  const [finalizado,    setFinalizado]    = useState(false);
  const [resultado,     setResultado]     = useState(null);
  const [cargando,      setCargando]      = useState(false);

  const intervalRef   = useRef(null);
  const mensajeIdxRef = useRef(0);

  // ── Progreso animado ─────────────────────────────────────────────────────
  const iniciarProgreso = () => {
    setProgreso(0);
    setFinalizado(false);
    setResultado(null);
    setMensajeActual(MENSAJES_PROGRESO[0]);
    mensajeIdxRef.current = 0;
    setModalVisible(true);

    intervalRef.current = setInterval(() => {
      setProgreso((prev) => {
        if (prev >= 85) { clearInterval(intervalRef.current); return prev; }
        const siguiente = prev + Math.random() * 2.5 + 0.5;
        const nuevoIdx  = Math.min(
          Math.floor(siguiente / (85 / MENSAJES_PROGRESO.length)),
          MENSAJES_PROGRESO.length - 1,
        );
        if (nuevoIdx !== mensajeIdxRef.current) {
          mensajeIdxRef.current = nuevoIdx;
          setMensajeActual(MENSAJES_PROGRESO[nuevoIdx]);
        }
        return Math.min(siguiente, 85);
      });
    }, 500);
  };

  const finalizarProgreso = (data) => {
    clearInterval(intervalRef.current);
    setMensajeActual("¡Carga completada!");
    setResultado(data);
    let p = 85;
    const finish = setInterval(() => {
      p += 3;
      setProgreso(Math.min(p, 100));
      if (p >= 100) { clearInterval(finish); setFinalizado(true); }
    }, 60);
  };

  const cerrarModal = () => {
    setModalVisible(false);
    setProgreso(0);
    setFinalizado(false);
    setResultado(null);
  };

  // ── Upload handler ───────────────────────────────────────────────────────
  const handleUpload = async (file) => {
    const tiposValidos = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!tiposValidos.includes(file.type)) {
      message.error("Solo se permiten archivos .xlsx o .xls");
      return false;
    }

    setCargando(true);
    iniciarProgreso();

    try {
      const data = await subirExcelInscritos(file);
      finalizarProgreso(data);
    } catch (error) {
      clearInterval(intervalRef.current);
      setModalVisible(false);
      message.error(
        error?.response?.data?.message || "Error al subir el archivo",
      );
    } finally {
      setCargando(false);
    }

    return false; // evita que Ant Design suba el archivo por su cuenta
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Card style={{ margin: 20 }}>
      <Title level={3}>Carga de Inscritos</Title>

      <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
        Sube el reporte Excel de hogares e inscritos al proyecto. Formatos
        aceptados </Text>

      <Upload
        accept=".xlsx,.xls"
        showUploadList={false}
        beforeUpload={handleUpload}
        disabled={cargando}
      >
        <Button
          type="primary"
          icon={<UploadOutlined />}
          loading={cargando}
          size="large"
        >
          {cargando ? "Procesando..." : "Seleccionar y subir Excel"}
        </Button>
      </Upload>

      {/* ── Modal de progreso ────────────────────────────────────────────── */}
      <Modal
        open={modalVisible}
        footer={
          finalizado
            ? <Button type="primary" onClick={cerrarModal}>Cerrar</Button>
            : null
        }
        closable={false}
        maskClosable={false}
        centered
        width={460}
        title={
          <Space>
            {finalizado
              ? <CheckCircleFilled style={{ color: "#52c41a", fontSize: 18 }} />
              : <SyncOutlined spin style={{ color: "#1677ff", fontSize: 18 }} />}
            <span>
              {finalizado ? "Carga completada" : "Cargando inscritos..."}
            </span>
          </Space>
        }
      >
        <div style={{ padding: "16px 0" }}>
          <Progress
            percent={Math.round(progreso)}
            status={finalizado ? "success" : "active"}
            strokeColor={
              finalizado
                ? "#52c41a"
                : { "0%": "#1677ff", "100%": "#36cfc9" }
            }
          />

          <div style={{ marginTop: 12, minHeight: 60 }}>
            {finalizado && resultado ? (
              <Space direction="vertical" size={4}>
                <Text strong style={{ color: "#52c41a" }}>
                  {resultado.message || "Carga completada exitosamente"}
                </Text>
                <Text type="secondary">
                  Total filas procesadas:{" "}
                  <Text strong>{resultado.total_filas ?? 0}</Text>
                </Text>
                <Text type="secondary">
                  Registros insertados:{" "}
                  <Text strong style={{ color: "#1677ff" }}>
                    {resultado.insertados ?? 0}
                  </Text>
                </Text>
                {(resultado.omitidos ?? 0) > 0 && (
                  <Text type="secondary">
                    Filas omitidas:{" "}
                    <Text strong style={{ color: "#faad14" }}>
                      {resultado.omitidos}
                    </Text>
                  </Text>
                )}
              </Space>
            ) : (
              <Text type="secondary">{mensajeActual}</Text>
            )}
          </div>
        </div>
      </Modal>
    </Card>
  );
};

export default ReporteKokamInscribir ;
